import { formatChartMonthKey } from '@/components/Dashboard/operacaoChart.utils'
import { formatWinRateDisplay } from '@/components/Dashboard/operacaoTab.utils'
import { stageConfig } from '@/components/Deals/deals-constants'
import type { DealStage } from '@/types'
import type { DashboardData } from '@/hooks/dashboardDataTypes'

/** Etapas que entram na previsão de fechamento (a partir de proposta enviada). */
export const CLOSING_FORECAST_STAGES: DealStage[] = [
  'proposta_enviada',
  'renegociacao',
  'ajustar_proposta',
  'gerar_contrato',
  'contrato_enviado',
  'handover',
]

export type ClosingForecast = {
  forecastValue: number
  forecastCount: number
  rawValue: number
  proposalsSentCount: number
  proposalsSentValue: number
  proposalsSentForecast: number
}

export type PipelineTabView = {
  totalRevenue: number
  totalWon: number
  avgTicket: number
  lostDeals: number
  winRate: number
  closedCount: number
  pipelineValue: number
  pipelineCount: number
  monthlyData: { month: string; count: number; revenue: number }[]
  closingForecast: ClosingForecast
}

export type PipelineClosingRow = {
  month: string
  receita: number
  deals: number
}

function normalizeDealStage(stage: string): DealStage | null {
  const key = stage.trim().toLowerCase() as DealStage
  return stageConfig[key] ? key : null
}

export function computeClosingForecast(
  dealsByStage: { stage: string; count: number; value: number }[],
): ClosingForecast {
  let forecastValue = 0
  let forecastCount = 0
  let rawValue = 0
  let proposalsSentCount = 0
  let proposalsSentValue = 0
  let proposalsSentForecast = 0

  for (const row of dealsByStage) {
    const etapa = normalizeDealStage(row.stage)
    if (!etapa || !CLOSING_FORECAST_STAGES.includes(etapa)) continue

    const probability = stageConfig[etapa].defaultProbability ?? 0
    const weighted = row.value * (probability / 100)

    forecastValue += weighted
    forecastCount += row.count
    rawValue += row.value

    if (etapa === 'proposta_enviada') {
      proposalsSentCount = row.count
      proposalsSentValue = row.value
      proposalsSentForecast = weighted
    }
  }

  return {
    forecastValue,
    forecastCount,
    rawValue,
    proposalsSentCount,
    proposalsSentValue,
    proposalsSentForecast,
  }
}

export function mapPipelineTabView(data: DashboardData): PipelineTabView | null {
  if (!data.sales || !data.summary) return null
  const wonDeals = data.sales.totalWon
  const lostDeals = data.summary.lostDeals
  return {
    totalRevenue: data.sales.totalRevenue,
    totalWon: wonDeals,
    avgTicket: data.sales.avgTicket,
    lostDeals,
    winRate: data.summary.taxaConversao,
    closedCount: wonDeals + lostDeals,
    pipelineValue: data.summary.pipelineValue,
    pipelineCount: data.summary.pipelineCount,
    monthlyData: data.sales.monthlyData,
    closingForecast: computeClosingForecast(data.summary.dealsByStage),
  }
}

export function buildPipelineClosingsChartData(
  monthlyData: PipelineTabView['monthlyData'],
): PipelineClosingRow[] {
  return monthlyData.map((row) => ({
    month: formatChartMonthKey(row.month),
    receita: row.revenue,
    deals: row.count,
  }))
}

export function hasPipelineClosingsData(rows: PipelineClosingRow[]): boolean {
  return rows.some((row) => row.receita > 0 || row.deals > 0)
}

export function shouldShowPipelineContextBanner(view: PipelineTabView): boolean {
  const noClosedRevenue = view.totalWon === 0 && view.totalRevenue === 0
  const hasOpenPipeline = view.pipelineCount > 0 || view.closingForecast.forecastCount > 0
  return noClosedRevenue && hasOpenPipeline
}

export function getPipelineWinRateDisplay(view: PipelineTabView, valuesVisible: boolean) {
  return formatWinRateDisplay(view.winRate, view.closedCount, valuesVisible)
}

export function buildWinLossMix(view: PipelineTabView) {
  return [
    { name: 'Ganhos', value: view.totalWon, color: '#10b981' },
    { name: 'Perdas', value: view.lostDeals, color: '#ef4444' },
  ].filter((row) => row.value > 0)
}

export type PipelineClosedDealRow = {
  id: string
  nome: string
  valor: number
  empresa: string
  owner: string
  dataFechamento: string | null
}
